/**
 * Chart kit: the renderer (SVG). Every chart in the game is drawn by it
 * (the 3D charts were removed 2026-10-21); the data comes from model.js.
 *
 * - Updates redraw at once (no animation).
 * - Hover, tap or arrow keys inspect one point/bar/slice with exact values.
 * - A "Data" table under the chart lists every value as text.
 */
import { DOWN, UP, dataRows, inspectLines, niceTicks, normalize, shortValue } from "./model.js";


const PAD = { l: 52, r: 14, t: 14, b: 28 };
const escAttr = (s) => String(s == null ? "" : s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

/** The SVG markup for model m at size w x h, with index `hot` highlighted. */
function svgMarkup(m, w, h, hot = -1) {
  if (m.kind === "donut") return donutSvg(m, w, h, hot);
  const iw = Math.max(10, w - PAD.l - PAD.r), ih = Math.max(10, h - PAD.t - PAD.b);
  const ticks = niceTicks(m.min, m.max, 4);
  const lo = ticks[0], hi = ticks[ticks.length - 1];
  const Y = (v) => PAD.t + ih - ((v - lo) / (hi - lo || 1)) * ih;
  const n = Math.max(1, m.x.length);
  const band = iw / n;
  const X = (i) => PAD.l + band * (i + 0.5);
  let g = "";
  // grid + y axis
  for (const t of ticks) {
    g += `<line class="ck-grid${t === 0 ? " zero" : ""}" x1="${PAD.l}" x2="${PAD.l + iw}" y1="${Y(t).toFixed(1)}" y2="${Y(t).toFixed(1)}"/>`;
    g += `<text class="ck-tick" x="${PAD.l - 6}" y="${(Y(t) + 3.5).toFixed(1)}" text-anchor="end">${escAttr(shortValue(t, m.unit))}</text>`;
  }
  // x labels, thinned to fit
  const every = Math.max(1, Math.ceil(n / Math.max(1, Math.floor(iw / 54))));
  m.x.forEach((lab, i) => {
    if (i % every === 0 || i === n - 1) g += `<text class="ck-tick" x="${X(i).toFixed(1)}" y="${h - 8}" text-anchor="middle">${escAttr(lab)}</text>`;
  });
  if (hot >= 0 && hot < n) g += `<rect class="ck-hot" x="${(PAD.l + band * hot).toFixed(1)}" y="${PAD.t}" width="${band.toFixed(1)}" height="${ih}"/>`;

  if (m.kind === "line" || m.kind === "area") {
    m.series.forEach((s, k) => {
      const pts = s.values.map((v, i) => (v == null ? null : [X(i), Y(v)]));
      const d = pts.reduce((acc, p, i) => (p ? acc + `${acc && pts[i - 1] ? "L" : "M"}${p[0].toFixed(1)},${p[1].toFixed(1)}` : acc), "");
      if (m.kind === "area") {
        const valid = pts.filter(Boolean);
        if (valid.length) g += `<path class="ck-area" fill="url(#ck-g${k})" d="${d}L${valid[valid.length - 1][0].toFixed(1)},${Y(Math.max(lo, 0)).toFixed(1)}L${valid[0][0].toFixed(1)},${Y(Math.max(lo, 0)).toFixed(1)}Z"/>`;
        g += `<defs><linearGradient id="ck-g${k}" x1="0" x2="0" y1="0" y2="1"><stop offset="0" stop-color="${s.color}" stop-opacity=".35"/><stop offset="1" stop-color="${s.color}" stop-opacity="0"/></linearGradient></defs>`;
      }
      g += `<path class="ck-line" stroke="${s.color}" d="${d}"/>`;
      if (hot >= 0 && pts[hot]) g += `<circle class="ck-dot" cx="${pts[hot][0].toFixed(1)}" cy="${pts[hot][1].toFixed(1)}" r="4" fill="${s.color}"/>`;
    });
  } else if (m.kind === "column" || m.kind === "stacked") {
    const k = m.series.length;
    const bw = m.kind === "stacked" ? band * 0.62 : (band * 0.72) / k;
    m.x.forEach((_, i) => {
      let up = 0, down = 0;
      m.series.forEach((s, j) => {
        const v = s.values[i] || 0;
        let y0, y1, x;
        if (m.kind === "stacked") {
          x = X(i) - bw / 2;
          if (v >= 0) { y0 = up; y1 = up + v; up = y1; } else { y0 = down; y1 = down + v; down = y1; }
        } else {
          x = X(i) - (band * 0.72) / 2 + j * bw;
          y0 = 0; y1 = v;
        }
        const top = Math.min(Y(y0), Y(y1)), hgt = Math.abs(Y(y1) - Y(y0));
        g += `<rect class="ck-bar" x="${x.toFixed(1)}" y="${top.toFixed(1)}" width="${Math.max(1, bw - 2).toFixed(1)}" height="${Math.max(0.5, hgt).toFixed(1)}" rx="2" fill="${s.color}"/>`;
      });
    });
  } else if (m.kind === "waterfall") {
    const bw = band * 0.64;
    m.bars.forEach((b, i) => {
      const top = Math.min(Y(b.from), Y(b.to)), hgt = Math.max(0.5, Math.abs(Y(b.to) - Y(b.from)));
      const fill = b.kind === "up" ? UP : b.kind === "down" ? DOWN : "#9fb4ff";
      g += `<rect class="ck-bar" x="${(X(i) - bw / 2).toFixed(1)}" y="${top.toFixed(1)}" width="${bw.toFixed(1)}" height="${hgt.toFixed(1)}" rx="2" fill="${fill}"/>`;
      if (i < m.bars.length - 1) g += `<line class="ck-link" x1="${(X(i) + bw / 2).toFixed(1)}" x2="${(X(i + 1) - bw / 2).toFixed(1)}" y1="${Y(b.to).toFixed(1)}" y2="${Y(b.to).toFixed(1)}"/>`;
    });
  } else if (m.kind === "candlestick") {
    const bw = Math.max(2, band * 0.6);
    (m.series[0] ? m.series[0].values : []).forEach((c, i) => {
      const col = c.c >= c.o ? UP : DOWN;
      g += `<line stroke="${col}" stroke-width="1" x1="${X(i).toFixed(1)}" x2="${X(i).toFixed(1)}" y1="${Y(c.h).toFixed(1)}" y2="${Y(c.l).toFixed(1)}"/>`;
      g += `<rect x="${(X(i) - bw / 2).toFixed(1)}" y="${Math.min(Y(c.o), Y(c.c)).toFixed(1)}" width="${bw.toFixed(1)}" height="${Math.max(1, Math.abs(Y(c.o) - Y(c.c))).toFixed(1)}" fill="${col}"/>`;
    });
  }
  return `<svg class="ck-svg" viewBox="0 0 ${w} ${h}" width="${w}" height="${h}" role="img" aria-label="${escAttr(m.title)}">${g}</svg>`;
}

function arcPath(cx, cy, r0, r1, a0, a1) {
  const p = (r, a) => `${(cx + r * Math.sin(a)).toFixed(2)},${(cy - r * Math.cos(a)).toFixed(2)}`;
  const large = a1 - a0 > Math.PI ? 1 : 0;
  return `M${p(r1, a0)}A${r1},${r1} 0 ${large} 1 ${p(r1, a1)}L${p(r0, a1)}A${r0},${r0} 0 ${large} 0 ${p(r0, a0)}Z`;
}

function donutSvg(m, w, h, hot) {
  const cx = Math.min(w, h * 1.1) / 2 + 4, cy = h / 2, r1 = Math.max(10, Math.min(cx, cy) - 8), r0 = r1 * 0.62;
  let a = 0, g = "";
  m.slices.forEach((s, i) => {
    const span = s.share * Math.PI * 2;
    if (span > 0.0005) {
      const grow = i === hot ? 5 : 0;
      g += `<path class="ck-slice" d="${arcPath(cx, cy, r0, r1 + grow, a, a + Math.min(span, Math.PI * 2 - 0.0001))}" fill="${s.color}"/>`;
    }
    a += span;
  });
  const center = hot >= 0 && m.slices[hot] ? `${(m.slices[hot].share * 100).toFixed(1)}%` : shortValue(m.total, m.unit);
  g += `<text class="ck-center" x="${cx}" y="${cy + 5}" text-anchor="middle">${escAttr(center)}</text>`;
  const lx = cx + r1 + 18;
  if (lx < w - 60) {
    m.slices.forEach((s, i) => {
      const y = 16 + i * 18;
      if (y > h - 6) return;
      g += `<rect x="${lx}" y="${y - 9}" width="10" height="10" rx="2" fill="${s.color}"/><text class="ck-legend${i === hot ? " on" : ""}" x="${lx + 16}" y="${y}">${escAttr(s.label)} · ${(s.share * 100).toFixed(1)}%</text>`;
    });
  }
  return `<svg class="ck-svg" viewBox="0 0 ${w} ${h}" width="${w}" height="${h}" role="img" aria-label="${escAttr(m.title)}">${g}</svg>`;
}

/** Index under a pointer at plot x (or donut angle). */
function hitIndex(m, w, h, px, py) {
  if (m.kind === "donut") {
    const cx = Math.min(w, h * 1.1) / 2 + 4, cy = h / 2;
    const r = Math.hypot(px - cx, py - cy), r1 = Math.min(cx, cy) - 8;
    if (r > r1 + 8 || r < r1 * 0.5) return -1;
    let ang = Math.atan2(px - cx, -(py - cy));
    if (ang < 0) ang += Math.PI * 2;
    let a = 0;
    for (let i = 0; i < m.slices.length; i++) { a += m.slices[i].share * Math.PI * 2; if (ang <= a) return i; }
    return -1;
  }
  const iw = w - PAD.l - PAD.r;
  const i = Math.floor(((px - PAD.l) / iw) * m.x.length);
  return i >= 0 && i < m.x.length ? i : -1;
}

function tooltipHtml(m, i) {
  const lines = inspectLines(m, i);
  if (!lines.length) return "";
  const head = m.kind === "donut" || m.kind === "waterfall" ? "" : `<b>${escAttr(m.x[i])}</b>`;
  return head + lines.map(([k, v, c]) => `<div>${c ? `<i style="background:${c}"></i>` : ""}<span>${escAttr(k)}</span><b>${escAttr(v)}</b></div>`).join("");
}

function tableHtml(m) {
  const [head, ...rows] = dataRows(m);
  return `<table class="ck-table"><thead><tr>${head.map((c) => `<th>${escAttr(c)}</th>`).join("")}</tr></thead><tbody>${rows.map((r) => `<tr>${r.map((c) => `<td>${escAttr(c)}</td>`).join("")}</tr>`).join("")}</tbody></table>`;
}

/**
 * Mount a 2D chart in `el`. Returns { update(spec), inspect(i), dispose(), model }.
 * opts.height sets the plot height (default: the element's, else 240).
 */
function mountSvgChart(el, spec, opts = {}) {
  el.classList.add("ck");
  el.innerHTML = `<div class="ck-plot" tabindex="0"></div><div class="ck-tip hidden" role="status"></div><details class="ck-data"><summary>Data</summary><div></div></details>`;
  const plot = el.querySelector(".ck-plot"), tip = el.querySelector(".ck-tip"), tableEl = el.querySelector(".ck-data > div");
  let model = normalize(spec), shown = model, hot = -1, disposed = false;
  const size = () => [Math.max(160, Math.round(plot.clientWidth || el.clientWidth || 600)), opts.height || Math.round(el.clientHeight) || 240];

  const draw = () => {
    const [w, h] = size();
    plot.innerHTML = svgMarkup(shown, w, h, hot);
  };
  const showTip = (i, px, py) => {
    hot = i;
    draw();
    if (i < 0) { tip.classList.add("hidden"); return; }
    tip.innerHTML = tooltipHtml(shown, i);
    tip.classList.remove("hidden");
    const [w] = size();
    tip.style.left = `${Math.min(Math.max(0, (px || 0) + 12), Math.max(0, w - 180))}px`;
    tip.style.top = `${Math.max(0, (py || 0) - 10)}px`;
  };
  const onMove = (e) => {
    const r = plot.getBoundingClientRect();
    const [w, h] = size();
    const px = ((e.clientX - r.left) / (r.width || w)) * w, py = ((e.clientY - r.top) / (r.height || h)) * h;
    showTip(hitIndex(shown, w, h, px, py), e.clientX - r.left, e.clientY - r.top);
  };
  plot.addEventListener("pointermove", onMove);
  plot.addEventListener("pointerdown", onMove);
  plot.addEventListener("pointerleave", () => showTip(-1));
  plot.addEventListener("keydown", (e) => {
    const n = shown.kind === "donut" ? shown.slices.length : shown.x.length;
    if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
      e.preventDefault();
      const next = hot < 0 ? 0 : (hot + (e.key === "ArrowRight" ? 1 : -1) + n) % n;
      showTip(next, 20, 10);
    } else if (e.key === "Escape") showTip(-1);
  });
  const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(() => !disposed && draw()) : null;
  if (ro) ro.observe(el);
  tableEl.innerHTML = tableHtml(model);
  draw();

  return {
    get model() { return model; },
    update(next) {
      // no animation: the new values draw at once (lighter on slow machines)
      model = shown = normalize(next);
      tableEl.innerHTML = tableHtml(model);
      draw();
    },
    inspect(i) { showTip(i, 20, 10); return tip.textContent; },
    dispose() { disposed = true; if (ro) ro.disconnect(); },
  };
}

export { hitIndex, mountSvgChart, svgMarkup, tableHtml, tooltipHtml };
